import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { sessionAPI } from '../api';
import { t } from '../i18n';
import toast from 'react-hot-toast';

// ─────────────────────────────────────────────────────────────────────────────
// Смены: цепочка «пресс → склад» целиком.
//
// Цифра выпуска рождается у пресса и меняется на складе, а спорят о ней потом:
// «оператор написал два брака, а приняли три», «этот лист сдавали высшим, а он
// второй». Пока обе половины лежали по разным экранам, ответить было нечем.
//
// Последнее слово остаётся за складом — он держит лист в руках. Но теперь
// видно, чьё это слово и от чего именно оно отличается.
// ─────────────────────────────────────────────────────────────────────────────

const STATES = {
  active:   { label: 'Прессуют',      color: 'var(--accent)'  },
  waiting:  { label: 'Ждёт приёмки',  color: 'var(--warning)' },
  accepted: { label: 'Принято',       color: 'var(--success)' },
  empty:    { label: 'Без листов',    color: 'var(--dim)'     },
};

// Значок события в ленте: половина «пресс» и половина «склад» должны
// различаться с одного взгляда, не вчитываясь в подпись.
const KINDS = {
  start:    { ic: '▶', color: 'var(--accent)'  },
  finish:   { ic: '■', color: 'var(--accent)'  },
  accept:   { ic: '✓', color: 'var(--success)' },
  regrade:  { ic: '⇄', color: 'var(--warning)' },
  revert:   { ic: '↩', color: 'var(--danger)'  },
  material: { ic: '±', color: 'var(--warning)' },
  quantity: { ic: '✎', color: 'var(--warning)' },
};

// Группа фильтров: рамка и подпись. Без них кнопки читаются одной лентой,
// и понять, где кончается «период» и начинается «состояние», можно только
// перебрав их по одной.
function FilterGroup({ label, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
      padding: '5px 8px 5px 10px', borderRadius: 10,
      border: '1px solid var(--border)', background: 'var(--bg)',
    }}>
      <span style={{
        color: 'var(--dim)', fontSize: 10.5, fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '.06em', marginRight: 2,
      }}>{t(label)}</span>
      {children}
    </div>
  );
}

const PERIODS = [
  { days: 7,   label: 'Неделя'   },
  { days: 30,  label: 'Месяц'    },
  { days: 90,  label: '3 месяца' },
  { days: 0,   label: 'Всё'      },
];

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};
const fmtFull = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};
const dur = (min) => {
  if (min === null || min === undefined) return '—';
  if (min < 60) return min + ' мин';
  return Math.floor(min / 60) + ' ч ' + (min % 60) + ' мин';
};

const errText = (err, fallback) => {
  const d = err && err.response && err.response.data;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (d.error) return String(d.error);
  const first = Object.values(d)[0];
  return Array.isArray(first) ? String(first[0]) : (first ? String(first) : fallback);
};

const th = {
  textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700,
  letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--dim)',
  borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
};
const td = {
  padding: '10px 12px', fontSize: 13, color: 'var(--text)',
  borderBottom: '1px solid var(--border)', verticalAlign: 'top',
};

// ── Одна смена целиком ───────────────────────────────────────────────────────
function SessionModal({ jobId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    sessionAPI.detail(jobId)
      .then((r) => { if (alive) setData(r.data); })
      .catch((err) => {
        toast.error(errText(err, t('Смена не найдена')));
        onClose();
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [jobId, onClose]);

  const params = data ? [
    ['Температура пресса', data.temperature],
    ['Давление',           data.pressure],
    ['Выдержка, сек',      data.hold_time],
    ['Цех: t° / влажность', data.air],
    ['Партия ДСП',         data.dsp_batch],
  ].filter((p) => p[1]) : [];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 9998,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card)', border: '1px solid var(--border)',
          borderRadius: 14, width: '100%', maxWidth: 980,
          maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column',
          boxShadow: 'var(--shadow)',
        }}
      >
        {/* Шапка */}
        <div style={{
          padding: '14px 18px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
              {loading ? t('Загрузка…') : data.product_name}
            </div>
            {data && (
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>
                {data.press_name} · {data.operator}
                {data.order_number !== '—' && ' · заказ ' + data.order_number}
                {data.client_name !== '—' && ' · ' + data.client_name}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="touch-btn"
            style={{
              background: 'transparent', border: 'none', color: 'var(--muted)',
              fontSize: 24, lineHeight: 1, cursor: 'pointer', padding: '0 4px',
            }}
          >×</button>
        </div>

        {loading || !data ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--dim)' }}>
            {t('Загрузка…')}
          </div>
        ) : (
          <div className="accept-grid" style={{ padding: 16, overflowY: 'auto' }}>
            {/* ── Левая колонка: что было у пресса и что стало у склада ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
                gap: 8,
              }}>
                {[
                  ['Начал',     fmt(data.started_at)],
                  ['Завершил',  fmt(data.completed_at)],
                  ['Длилась',   dur(data.duration_min)],
                  ['План',      data.planned],
                  ['Выпуск',    data.done],
                  ['Листов',    data.sheets_total],
                ].map(([k, v]) => (
                  <div key={k} style={{
                    background: 'var(--bg)', border: '1px solid var(--border)',
                    borderRadius: 8, padding: '8px 10px',
                  }}>
                    <div style={{ fontSize: 10, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{t(k)}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginTop: 2 }}>{v}</div>
                  </div>
                ))}
              </div>

              {/* Главная пара цифр — из-за неё и открывают смену */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 6 }}>
                  {t('Пресс сказал / склад принял')}
                </div>
                {data.compare.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--dim)' }}>{t('Сортов нет')}</div>
                ) : (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                    {data.compare.map((r) => {
                      const diff = r.press !== r.warehouse;
                      return (
                        <div key={r.name} style={{
                          display: 'grid', gridTemplateColumns: '1fr 56px 20px 56px',
                          alignItems: 'center', gap: 4, padding: '8px 10px',
                          borderTop: '1px solid var(--border)',
                          background: diff ? 'rgba(224,165,58,.10)' : 'transparent',
                        }}>
                          <div style={{ fontSize: 13, color: 'var(--text)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                          <div style={{ textAlign: 'right', fontSize: 14, fontWeight: 700, color: 'var(--muted)' }}>{r.press}</div>
                          <div style={{ textAlign: 'center', color: 'var(--dim)' }}>→</div>
                          <div style={{ textAlign: 'right', fontSize: 14, fontWeight: 800, color: diff ? 'var(--warning)' : 'var(--text)' }}>{r.warehouse}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 6, lineHeight: 1.45 }}>
                  {t('У пресса лист смотрят мельком и на горячую. Окончательный сорт решает склад — его цифра справа.')}
                </div>
              </div>

              {data.materials.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 6 }}>
                    {t('Брак материала (итог)')}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {data.materials.map((m) => (
                      <div key={m.name} style={{
                        background: 'var(--bg)', border: '1px solid var(--border)',
                        borderRadius: 8, padding: '6px 10px', fontSize: 13, color: 'var(--text)',
                      }}>
                        {m.name}: <b>{m.quantity}</b>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {params.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 6 }}>
                    {t('Режим прессования')}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {params.map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
                        <span style={{ color: 'var(--dim)' }}>{t(k)}</span>
                        <span style={{ color: 'var(--text)' }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Правая колонка: лента событий ── */}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 8 }}>
                {t('Что происходило')}
              </div>
              {data.events.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--dim)' }}>{t('Событий нет')}</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {data.events.map((e, i) => {
                    const k = KINDS[e.kind] || { ic: '·', color: 'var(--dim)' };
                    const last = i === data.events.length - 1;
                    return (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '24px 1fr', gap: 10 }}>
                        {/* Вертикаль ленты: видно, что события одной цепочки */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <div style={{
                            width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                            border: '1px solid ' + k.color, color: k.color,
                            display: 'grid', placeItems: 'center', fontSize: 11,
                          }}>{k.ic}</div>
                          {!last && <div style={{ flex: 1, width: 1, background: 'var(--border)', minHeight: 12 }} />}
                        </div>
                        <div style={{ paddingBottom: last ? 0 : 14, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{e.title}</div>
                          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2, wordBreak: 'break-word' }}>{e.text}</div>
                          <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 3 }}>
                            {fmtFull(e.at)} · {e.who}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {data.pending > 0 && (
                <div style={{
                  marginTop: 12, padding: '8px 10px', borderRadius: 8,
                  background: 'rgba(224,165,58,.12)', border: '1px solid var(--warning)',
                  fontSize: 12, color: 'var(--text)',
                }}>
                  {t('Ещё не принято складом')}: <b>{data.pending}</b> {t('шт')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Страница ─────────────────────────────────────────────────────────────────
export default function SessionHistory() {
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [days, setDays]       = useState(30);
  const [state, setState]     = useState('');
  const [q, setQ]             = useState('');
  const [query, setQuery]     = useState('');
  const [limit, setLimit]     = useState(50);
  const [openId, setOpenId]   = useState(null);

  // Поиск дёргаем не на каждую букву: список тяжёлый, а печатают в цеху
  useEffect(() => {
    const id = setTimeout(() => setQuery(q.trim()), 400);
    return () => clearTimeout(id);
  }, [q]);

  const load = useCallback(() => {
    setLoading(true);
    sessionAPI.list({ days, q: query || undefined, limit })
      .then((r) => {
        setRows(r.data.results || []);
        setTotal(r.data.count || 0);
      })
      .catch((err) => toast.error(errText(err, t('Не удалось загрузить историю'))))
      .finally(() => setLoading(false));
  }, [days, query, limit]);

  useEffect(() => { load(); }, [load]);

  const shown = state ? rows.filter((r) => r.state === state) : rows;

  const btn = (active) => ({
    padding: '7px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
    border: '1px solid ' + (active ? 'var(--accent)' : 'var(--border)'),
    background: active ? 'var(--accent-weak)' : 'var(--card)',
    color: active ? 'var(--text)' : 'var(--muted)',
  });

  return (
    <Layout title={t('Смены')}>
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', maxWidth: 760, lineHeight: 1.5 }}>
          {t('Вся цепочка одной смены: кто встал к прессу и что насчитал, кто принял на складе и что переписал. Последнее слово за складом — он держит лист в руках.')}
        </div>

        {/* Фильтры */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('Продукт, заказ, пресс, оператор…')}
            style={{
              flex: '1 1 260px', minWidth: 200, padding: '8px 12px', borderRadius: 8,
              border: '1px solid var(--border)', background: 'var(--input)',
              color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
            }}
          />
          {/* Период и состояние — разные вопросы, а кнопки шли одной лентой:
              девять одинаковых прямоугольников подряд, и «3 месяца» стояло
              вплотную к «Прессуют». Глаз их не разделяет, и человек ищет,
              какая из них что делает, каждый раз заново. */}
          <FilterGroup label="Период">
            {PERIODS.map((p) => (
              <button key={p.days} onClick={() => setDays(p.days)}
                      className="touch-btn" style={btn(days === p.days)}>
                {t(p.label)}
              </button>
            ))}
          </FilterGroup>

          <FilterGroup label="Состояние">
            <button onClick={() => setState('')} className="touch-btn" style={btn(!state)}>
              {t('Все')}
            </button>
            {Object.keys(STATES).map((k) => (
              <button key={k} onClick={() => setState(state === k ? '' : k)}
                      className="touch-btn" style={btn(state === k)}>
                <span style={{ color: STATES[k].color, marginRight: 5 }}>●</span>
                {t(STATES[k].label)}
              </button>
            ))}
          </FilterGroup>

          <button onClick={load} className="touch-btn"
                  style={{ ...btn(false), marginLeft: 'auto' }}>⟳ {t('Обновить')}</button>
        </div>

        {/* Таблица */}
        <div style={{
          background: 'var(--card)', border: '1px solid var(--border)',
          borderRadius: 12, overflow: 'hidden',
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={th}>{t('Смена')}</th>
                  <th style={th}>{t('Продукт')}</th>
                  <th style={th}>{t('Оператор')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('План / выпуск')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('Листов')}</th>
                  <th style={th}>{t('Принял склад')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('Правок')}</th>
                  <th style={th}>{t('Состояние')}</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td style={{ ...td, textAlign: 'center', color: 'var(--dim)', padding: 30 }} colSpan={8}>
                    {t('Загрузка…')}
                  </td></tr>
                )}
                {!loading && shown.length === 0 && (
                  <tr><td style={{ ...td, textAlign: 'center', color: 'var(--dim)', padding: 30 }} colSpan={8}>
                    {t('Смен за этот период нет')}
                  </td></tr>
                )}
                {!loading && shown.map((r) => {
                  const st = STATES[r.state] || STATES.empty;
                  const edits = r.regrades + r.mat_changes + (r.qty_changes || 0);
                  return (
                    <tr
                      key={r.job_id}
                      onClick={() => setOpenId(r.job_id)}
                      className="touch-btn"
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{fmt(r.started_at)}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--dim)' }}>
                          {r.press_name} · {dur(r.duration_min)}
                        </div>
                      </td>
                      <td style={td}>
                        <div style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.product_name}
                        </div>
                        <div style={{ fontSize: 11.5, color: 'var(--dim)' }}>
                          {r.order_number !== '—' ? t('заказ') + ' ' + r.order_number : r.texture_name}
                        </div>
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.operator}</td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <span style={{ color: 'var(--dim)' }}>{r.planned}</span>
                        <span style={{ color: 'var(--dim)', margin: '0 4px' }}>/</span>
                        <b>{r.done}</b>
                      </td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <b>{r.sheets_total}</b>
                        {r.pending > 0 && (
                          <div style={{ fontSize: 11.5, color: 'var(--warning)' }}>
                            {t('ждёт')} {r.pending}
                          </div>
                        )}
                      </td>
                      <td style={td}>
                        {r.accepted_qty > 0 ? (
                          <>
                            <div><b>{r.accepted_qty}</b> {t('шт')} · {r.accepted_by}</div>
                            <div style={{ fontSize: 11.5, color: 'var(--dim)' }}>{fmt(r.accepted_at)}</div>
                          </>
                        ) : <span style={{ color: 'var(--dim)' }}>—</span>}
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        {edits > 0 ? (
                          <span style={{
                            background: 'rgba(224,165,58,.15)', color: 'var(--warning)',
                            borderRadius: 20, padding: '2px 9px', fontSize: 12, fontWeight: 700,
                          }}>{edits}</span>
                        ) : <span style={{ color: 'var(--dim)' }}>—</span>}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <span style={{ color: st.color, fontSize: 12, fontWeight: 600 }}>
                          ● {t(st.label)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12, color: 'var(--dim)' }}>
            {t('Показано')} {shown.length} {t('из')} {total}
          </div>
          {rows.length < total && (
            <button onClick={() => setLimit(limit + 50)} className="touch-btn" style={btn(false)}>
              {t('Показать ещё')}
            </button>
          )}
        </div>
      </div>

      {openId && <SessionModal jobId={openId} onClose={() => setOpenId(null)} />}
    </Layout>
  );
}
